import {HttpClient} from '@angular/common/http';
import {inject, Injectable} from '@angular/core';
import {concatMap, Observable, of, Subscriber, tap} from 'rxjs';
import {ConfirmSignInOutput} from "aws-amplify/auth";
import {CognitoService} from "./cognito.service";
import {AuthConnection} from "../types/auth-types";
import { environment } from "environments/environment";
import {UrlService} from "./url.service";
import {UrlEnum} from "../types/url-enum";
import {AppDB} from "app/core/indexDB/db";

@Injectable({ providedIn: 'root' })
export class AuthService {
    private _httpClient = inject(HttpClient);
    private _urlService: UrlService = inject(UrlService);
    private _cognitoService: CognitoService = inject(CognitoService);
    private _authConnectionData: AuthConnection;

    constructor() {
        //init with default data
        if(!this._authConnectionData){
            this._authConnectionData = {
                tenantId: environment.defaultAuth.tennantID,
                userPoolId: '',
                region: '',
                userPoolClientId: '',
                ownerEmailAddress: ''
            };
        }

        this._cognitoService.resetSignInError();
        this._cognitoService.signedOutEvt$.subscribe(value => {
            if(value){
                this.clearLocalData();
            }
        })
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
    set accessToken(token: string) {
        localStorage.setItem('accessToken', token);
    }

    get accessToken(): string {
        return localStorage.getItem('accessToken') ?? '';
    }

    get identityToken(): string {
        const key:string[] = Object.keys(localStorage).filter(key => key.endsWith('.idToken'));
        if(key && key.length > 0){
            return localStorage.getItem(key[0]) ?? '';
        }
        return ""
    }

    get authConnectionData(): AuthConnection {
        if(!this._authConnectionData){
            this._authConnectionData = {
                tenantId: environment.defaultAuth.tennantID,
                userPoolId: '',
                region: '',
                userPoolClientId: '',
                ownerEmailAddress: ''
            };
        }
        return this._authConnectionData;
    }


    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Forgot password
     *
     * @param email
     * @param clinic
     */
    forgotPassword(email: string, clinic:string): Observable<any> {
        return this.getUserPoolInfo(clinic)
            .pipe(
                concatMap(authData => {
                    this.configureAWSWithAuthData(authData);

                    return new Observable((observer:Subscriber<string>) => {
                        let promise:Promise<void> = this._cognitoService.handleResetPassword(email);
                        promise.then(result => {
                            observer.next();
                            observer.complete();
                        }).catch( error => {
                            observer.error(error);
                        });
                    });
                }));
    }

    private configureAWSWithAuthData(conData: AuthConnection) {
        let cognito = {
            Cognito: {
                userPoolClientId: conData.userPoolClientId,
                userPoolId: conData.userPoolId,
                region: conData.region,
            }
        };
        this._cognitoService.configureAWS(cognito);
    }

    /**
     * Reset password
     *
     * @param username
     * @param newPass
     * @param code
     */
    async resetPassword(username: string, newPass: string, code: string): Promise<void> {
        await this._cognitoService.handleConfirmResetPassword(username, newPass, code);
    }

    /**
     * Confirm password - use this interface after account activation to reset temporary password
     *
     * @param password
     */
    confirmSignInNewPassword(password: string): Observable<string> {
        return new Observable((observer:Subscriber<string>) => {
            let promise:Promise<ConfirmSignInOutput> = this._cognitoService.confirmSignIn(password);
            promise.then(result => {
                observer.next(result.nextStep.signInStep)
                observer.complete();
            }).catch( error => {
                observer.error(error);
            });
        });
    }

    /**
     * Sign in
     *
     * @param email
     * @param password
     * @param clinic
     */
    signIn( email: string, password: string, clinic: string ): Observable<string> {
        return this.getUserPoolInfo(clinic)
            .pipe(
            concatMap(authData => {
                this.configureAWSWithAuthData(authData);

                return new Observable((observer:Subscriber<string>) => {
                    let promise:Promise<string> = this._cognitoService.handleSignIn(email, password);
                    promise.then(result => {
                        observer.next(result);
                        observer.complete();
                    }).catch( error => {
                        observer.error(error);
                    });
                });
            }));
    }

    async refresh(): Promise<void> {
        await this._cognitoService.updateSignInData();
    }

    isAuthenticated(): Promise<boolean> {
        if(this._authConnectionData?.userPoolClientId?.length < 1) { //no data or
            let conData = localStorage.getItem('connectionData');
            if(conData){
                this._authConnectionData = JSON.parse(conData);
                this.configureAWSWithAuthData(this._authConnectionData);
            }
            else{
                return Promise.resolve(false);
            }
        }
        return this._cognitoService.checkLoggedIn();
    }

    /**
     * Sign out
     */
    async signOut(): Promise<void> {
        await this._cognitoService.handleSignOut();
    }

    public clearLocalData(){
        const colorScheme = localStorage.getItem('UI_COLOR_SCHEME');
        const clinics = localStorage.getItem('AVAILABLE_CLINICS');
        const lastClinic = localStorage.getItem('LAST_CLINIC');
        localStorage.clear();
        //save only color scheme
        localStorage.setItem('UI_COLOR_SCHEME', colorScheme);
        localStorage.setItem('AVAILABLE_CLINICS', clinics);
        localStorage.setItem('LAST_CLINIC', lastClinic);
        AppDB.getDB().deleteDatabase();
        this._authConnectionData=undefined;
        this._cognitoService.clearAmplify();
    }

    /**
     * Sign up
     *
     * @param SignUpReq
     */
    signUp(SignUpReq: {
        "phoneNumber": "string";
        "name": "string";
        "email": "string";
        "address": "string";
        "cui": "string";
        "companyName": "string";
        "commerceRegisterId": "string";
        "platitorTVA": "string"
    }): Observable<any> {
        return this._httpClient.post(this._urlService.getUrl(UrlEnum.public_clinicSignUp), SignUpReq);
    }

    /**
     * Unlock session
     *
     * @param credentials
     */
    unlockSession(credentials: {
        email: string;
        password: string;
    }): Observable<any> {
        return this._httpClient.post('api/auth/unlock-session', credentials);
    }


    private getUserPoolInfo(clinicName:string): Observable<AuthConnection> {
        return this._httpClient.post<AuthConnection>(this._urlService.getUrl(UrlEnum.public_searchByClinicName),
            {
                name: clinicName
            },
        ).pipe(
            concatMap(response => of(response)),
        ).pipe(
            tap(authConnectionData => {
                this._authConnectionData = (authConnectionData);
                localStorage.setItem('connectionData', JSON.stringify(this._authConnectionData));
            })
        );
    }


}
